import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Descope, useDescope, useSession, useUser } from '@descope/react-sdk'
import { GlobalLoading } from '@nxt-platform/global-components'
import { Alert, AlertDescription, AlertTitle } from '@nxt-shared/ui/components/Alert'
import { Badge } from '@nxt-shared/ui/components/Badge'
import { Button } from '@nxt-shared/ui/components/Button'
import { Card } from '@nxt-shared/ui/components/Card'
import {
  platformManagementAcceptInvitationMutation,
  platformManagementInvitationMetaOptions,
} from '../api/@tanstack/react-query.gen.ts'
import { usePageTitle } from '../features/page-title/pageTitle'

// Invitation landing (rework-invite-flow). Deliberately OUTSIDE the /me layout (me_. prefix): the
// visitor typically has no session yet — this page renders the invitation summary first and embeds
// the existing sign-up-or-in flow instead of the layout's generic login gate.
export const Route = createFileRoute('/me_/invite')({
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: InviteLandingRoute,
})

function LandingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-background flex items-start justify-center px-4 py-10 lg:py-16">
      <div className="w-full max-w-lg space-y-4">{children}</div>
    </div>
  )
}

function InvalidCard({ title, description }: { title: string; description: string }) {
  return (
    <Card className="p-6 space-y-2">
      <h1 className="text-lg font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Card>
  )
}

function InviteLandingRoute() {
  const { token } = Route.useSearch()
  usePageTitle('Invitation')

  const meta = useQuery({
    ...platformManagementInvitationMetaOptions({ query: { token } }),
    enabled: token.length > 0,
    staleTime: 30_000,
    retry: 1,
  })

  if (!token) {
    return (
      <LandingShell>
        <InvalidCard
          title="Invalid invitation link"
          description="This link is missing its invitation token. Please use the link from your invitation email."
        />
      </LandingShell>
    )
  }

  if (meta.isPending) return <GlobalLoading />

  if (meta.isError || !meta.data) {
    return (
      <LandingShell>
        <InvalidCard
          title="Something went wrong"
          description="The invitation could not be loaded. Please try again in a moment."
        />
      </LandingShell>
    )
  }

  const info = meta.data

  if (info.state === 'invalid') {
    return (
      <LandingShell>
        <InvalidCard
          title="This invitation is no longer valid"
          description="It may have been revoked or replaced. Ask your administrator to send you a new invitation."
        />
      </LandingShell>
    )
  }

  if (info.state === 'expired') {
    return (
      <LandingShell>
        <InvalidCard
          title="This invitation has expired"
          description="Ask your administrator to send you a new invitation."
        />
      </LandingShell>
    )
  }

  if (info.state === 'accepted') {
    return (
      <LandingShell>
        <Card className="p-6 space-y-4">
          <h1 className="text-lg font-semibold">Invitation already accepted</h1>
          <p className="text-sm text-muted-foreground">This invitation has already been used.</p>
          <Button icon={ArrowRightIcon} onClick={() => (window.location.href = '/me')}>Go to your organizations</Button>
        </Card>
      </LandingShell>
    )
  }

  return <PendingInvite token={token} info={info} />
}

function PendingInvite({
  token,
  info,
}: {
  token: string
  info: {
    descopeId?: string
    tenantName?: string | null
    organizationLabels?: string[]
    email?: string
    displayName?: string
  }
}) {
  const { isAuthenticated, isSessionLoading } = useSession()
  const tenantLabel = info.tenantName ?? 'a Tickaroo tenant'

  return (
    <LandingShell>
      <Card className="p-6 space-y-3">
        <h1 className="text-lg font-semibold">You have been invited to join {tenantLabel}</h1>
        {(info.organizationLabels?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2">
            {info.organizationLabels!.map((label) => (
              <Badge key={label} variant="secondary">
                {label}
              </Badge>
            ))}
          </div>
        )}
        {info.email && (
          <p className="text-sm text-muted-foreground">
            Invitation sent to <span className="font-medium">{info.email}</span>
          </p>
        )}
      </Card>

      {isSessionLoading ? (
        <GlobalLoading />
      ) : isAuthenticated ? (
        <AcceptCard token={token} invitedEmail={info.email} tenantLabel={tenantLabel} />
      ) : (
        <Card className="p-6 space-y-3">
          <p className="text-sm text-muted-foreground">
            Sign in — or create an account — to accept the invitation.
          </p>
          {/* Deliberately NO tenant pre-selection: the visitor is not a member of the inviting
              tenant until they accept, and the flow's sign-up branch rejects a selected tenant for
              non-members (Descope E061007 "Not authorized for selected tenant", staging QA defect 1).
              Sign-up therefore runs under project-level auth policy; the tenant's own settings apply
              from their first tenant-scoped login onward. The invited address and display name
              prefill the flow's inputs (`email`/`loginId` and the sign-up screen's `fullName`);
              everything stays editable — accepting with a different account is allowed and
              disclosed on the accept card. */}
          <Descope
            flowId="sign-up-or-in"
            form={info.email || info.displayName ? {
              ...(info.email ? {email: info.email, loginId: info.email} : {}),
              ...(info.displayName ? {fullName: info.displayName, name: info.displayName} : {})
            } : undefined}
          />
        </Card>
      )}
    </LandingShell>
  )
}

function AcceptCard({
  token,
  invitedEmail,
  tenantLabel,
}: {
  token: string
  invitedEmail?: string
  tenantLabel: string
}) {
  const navigate = useNavigate()
  const sdk = useDescope()
  const { user } = useUser()
  const sessionEmail = user?.email

  const accept = useMutation({
    ...platformManagementAcceptInvitationMutation(),
    onSuccess: async () => {
      // The new tenant membership/roles land in the JWT only after a refresh; do it before /me
      // loads so the overview immediately shows the new organizations.
      await sdk.refresh()
      await navigate({ to: '/me' })
    },
  })

  const emailMismatch = Boolean(invitedEmail && sessionEmail && invitedEmail.toLowerCase() !== sessionEmail.toLowerCase())

  return (
    <Card className="p-6 space-y-4">
      {emailMismatch && (
        <Alert>
          <AlertTitle>Different account</AlertTitle>
          <AlertDescription>
            This invitation was sent to <span className="font-medium">{invitedEmail}</span>, but you are signed in as{' '}
            <span className="font-medium">{sessionEmail}</span>. Accepting grants access to your signed-in account.
          </AlertDescription>
        </Alert>
      )}
      {accept.isError && (
        <Alert variant="danger">
          <AlertTitle>Could not accept the invitation</AlertTitle>
          <AlertDescription>
            The invitation may no longer be valid. Ask your administrator to send a new one.
          </AlertDescription>
        </Alert>
      )}
      <p className="text-sm text-muted-foreground">
        Accept to get access to {tenantLabel}
        {sessionEmail ? (
          <>
            {' '}
            as <span className="font-medium">{sessionEmail}</span>
          </>
        ) : null}
        .
      </p>
      <Button onClick={() => accept.mutate({ body: { token } })} disabled={accept.isPending}>
        {accept.isPending ? 'Accepting…' : 'Accept invitation'}
      </Button>
    </Card>
  )
}
